import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Search,
  X,
  Menu,
  Disc,
  Users,
  UserCog,
  LogOut,
  LogIn,
  ChevronRight,
  LayoutDashboard,
  Music,
  Globe,
  Library,
  ListMusic,
  Database,
  Building2,
  Mic2,
  Disc3,
  CalendarDays,
  FilePlus,
  Mail,
  BarChart3,
  FileSpreadsheet,
  MessageSquare,
  Megaphone,
  HelpCircle,
  Users2,
  ShieldCheck,
  Headphones,
  Radio,
  User,
  Shield,
  BrainCircuit,
  Bell,
  PiggyBank,
  Crown,
  ArrowRightLeft,
  FileText,
  Receipt,
  Star,
} from 'lucide-react';
import axios from 'axios';
import { authAction, authSelector } from '../features/auth/slice';
import useAuthInit from '../hooks/useAuthInit';
import { PUBLIC_BASE } from '../pages/public/config';
import UserAvatar from './UserAvatar';
import { useMessage } from '../contexts/MessageContext';
import Footer from './Footer/Footer';

const LOGO_URL =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/logo.png';
const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const ICON_MAP = {
  '대시보드': LayoutDashboard,
  '당월권리사별': Users,
  '당월앨범별': Disc,
  '당월곡별': Music,
  '당월플랫폼별': Globe,
  '당월아티스트별': User,
  '앨범별누적': Library,
  '곡별누적': ListMusic,
  '권리사': Building2,
  '아티스트': Mic2,
  '앨범': Disc3,
  '곡': Music,
  '월정산': CalendarDays,
  '대량등록': FilePlus,
  '권리사메일발송': Mail,
  '공지사항': Megaphone,
  '자유게시판': MessageSquare,
  '질문게시판': HelpCircle,
  '협업게시판': Users2,
  '음원심의': Headphones,
};

const SECTION_ICON_MAP = {
  '정산리포트': BarChart3,
  '데이터관리': Database,
  '엑셀업로드': FileSpreadsheet,
  '커뮤니티': MessageSquare,
  '방송심의': Radio,
};

const SECTION_GRADIENT = {
  '정산리포트': { id: 'stl-g0', colors: ['#6366f1', '#3b82f6'] },
  '데이터관리': { id: 'stl-g1', colors: ['#9333ea', '#c026d3'] },
  '엑셀업로드': { id: 'stl-g2', colors: ['#84cc16', '#10b981'] },
  '커뮤니티': { id: 'stl-g3', colors: ['#60a5fa', '#a78bfa'] },
  '방송심의': { id: 'stl-g4', colors: ['#ef4444', '#f97316'] },
};

function SettlementLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { me, menuItems, distributorTheme, distributorName, distributorLogo, distributorId } = useSelector(authSelector.selectAuthState);
  const isPink = distributorId === 1;
  const isDark = distributorTheme === 'dark';
  const isSuperAdminMode = me?.superAdminYn === 'Y' && isPink && pathname.startsWith('/super') && pathname !== '/super/promotion-report';
  const { loading: authLoading } = useAuthInit();

  // 타이틀 설정 (핑크/비핑크 모두)
  useEffect(() => {
    if (!distributorName) return;
    if (isPink) {
      document.title = 'p!nk 정산시스템';
    } else {
      document.title = distributorName;
    }
  }, [isPink, distributorName]);

  // 비핑크 유통사: KDIST 파비콘 + OG 메타태그
  useEffect(() => {
    if (isPink) return;
    const kdistSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#0ea5e9"/><rect x="5" y="19" width="5" height="8" rx="1.5" fill="white" fill-opacity="0.55"/><rect x="13.5" y="14" width="5" height="13" rx="1.5" fill="white"/><rect x="22" y="9" width="5" height="18" rx="1.5" fill="white" fill-opacity="0.55"/><path d="M7.5 19 L16 13.5 L24.5 8.5" stroke="white" stroke-width="1.6" stroke-linecap="round"/><circle cx="7.5" cy="19" r="1.8" fill="white"/><circle cx="16" cy="13.5" r="1.8" fill="white"/><circle cx="24.5" cy="8.5" r="2.2" fill="white"/></svg>`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = `data:image/svg+xml,${encodeURIComponent(kdistSvg)}`;
    const name = distributorName || '정산시스템';
    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', `${name} 정산시스템`);
    setMeta('og:description', `${name} 음원 정산시스템`);
    setMeta('og:site_name', name);
  }, [isPink, distributorId, distributorName, isDark]);

  const [mobileOpen, setMobileOpen] = useState(false);
  const { unreadCount, openInbox } = useMessage() || {};

  const [hasPromotionReport, setHasPromotionReport] = useState(false);
  useEffect(() => {
    if (!me || me.adminYn === 'Y') return;
    axios.get('/api/promotion-report/my')
      .then(({ data }) => setHasPromotionReport((data.rows || []).length > 0))
      .catch(() => {});
  }, [me]);

  // 검색 상태
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setSearchResults(null);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await axios.get('/public/search', { params: { q } });
      setSearchResults(res.data);
    } catch {
      setSearchResults({ albums: [], artists: [] });
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setSearchResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearchResults(null);
  };

  const handleSearchBlur = () => {
    setTimeout(() => setSearchResults(null), 200);
  };

  const handleResultClick = (type, id) => {
    clearSearch();
    if (type === 'album') navigate(`${PUBLIC_BASE}/albums/${id}`);
    else navigate(`${PUBLIC_BASE}/artists/${id}`);
  };

  const hasResults =
    searchResults &&
    (searchResults.albums?.length > 0 || searchResults.artists?.length > 0);

  const logout = () => {
    dispatch(authAction.logout());
    localStorage.removeItem('token');
    navigate('/login');
  };

  // 대시보드(/)는 별도 링크이므로 섹션 드롭다운에서 제외
  const filterSectionItems = (items) =>
    items ? items.filter((item) => item.url !== '/') : [];

  // 현재 경로가 특정 섹션에 속하는지 확인
  const isSectionActive = (section) => {
    const filtered = filterSectionItems(section.items);
    return filtered.some((item) => item.url && item.url === pathname);
  };

  const isMenuActive = (url) => url && url === pathname;

  const SearchDropdown = () =>
    searchLoading || hasResults || (searchResults && !hasResults) ? (
      <div className={`absolute top-full mt-1 left-0 right-0 ${searchDropdownBg} border rounded-xl shadow-2xl overflow-hidden z-50`}>
        {searchLoading ? (
          <div className={`flex items-center justify-center gap-2 py-4 ${textSecondary} text-sm`}>
            <div className="w-3 h-3 rounded-full border-2 border-sky-400 border-t-transparent animate-spin" />
            검색 중...
          </div>
        ) : !hasResults ? (
          <p className={`text-center ${textMuted} text-sm py-4`}>결과 없음</p>
        ) : (
          <div>
            {searchResults.albums?.length > 0 && (
              <div className="p-2">
                <p className={`text-[10px] ${textMuted} uppercase tracking-widest px-2 pb-1`}>앨범</p>
                {searchResults.albums.map((album) => (
                  <button
                    key={album.id}
                    onMouseDown={() => handleResultClick('album', album.id)}
                    className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg ${searchItemHover} text-left transition-colors`}>
                    <img src={album.image || FALLBACK_IMG} alt={album.nameKr} className="w-8 h-8 rounded object-cover shrink-0" />
                    <div className="min-w-0">
                      <p className={`${textPrimary} text-xs font-medium truncate`}>{album.nameKr}</p>
                      <p className={`${textMuted} text-[11px] truncate`}>{album.artist?.nameKr}</p>
                    </div>
                    <Disc size={13} className={`${textMuted} shrink-0 ml-auto`} />
                  </button>
                ))}
              </div>
            )}
            {searchResults.artists?.length > 0 && (
              <div className={`p-2 border-t ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                <p className={`text-[10px] ${textMuted} uppercase tracking-widest px-2 pb-1`}>아티스트</p>
                {searchResults.artists.map((artist) => (
                  <button
                    key={artist.id}
                    onMouseDown={() => handleResultClick('artist', artist.id)}
                    className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg ${searchItemHover} text-left transition-colors`}>
                    <img src={artist.image || FALLBACK_IMG} alt={artist.nameKr} className="w-8 h-8 rounded-full object-cover shrink-0" />
                    <p className={`${textPrimary} text-xs font-medium truncate`}>{artist.nameKr}</p>
                    <Users size={13} className={`${textMuted} shrink-0 ml-auto`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    ) : null;

  // 테마 클래스 세트
  const bg = isDark ? 'bg-[#0d1526]' : 'bg-slate-100';
  const headerBg = isDark ? 'bg-[#0d1526]/95' : 'bg-white/95';
  const borderColor = isDark ? 'border-white/10' : 'border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-slate-500';
  const textMuted = isDark ? 'text-slate-500' : 'text-slate-400';
  const navLinkActive = isDark ? 'text-white border-sky-400' : 'text-sky-600 border-sky-500';
  const navLinkInactive = isDark ? 'text-slate-400 border-transparent hover:text-slate-200' : 'text-slate-500 border-transparent hover:text-slate-700';
  const inputBg = isDark
    ? 'bg-[#1a2b42] border-white/[0.08] text-slate-200 placeholder-slate-500'
    : 'bg-slate-100 border-slate-200 text-slate-800 placeholder-slate-400';
  const searchDropdownBg = isDark ? 'bg-[#1a2b42] border-white/10' : 'bg-white border-slate-200';
  const searchItemHover = isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50';
  const mobileMenuBg = isDark ? 'bg-[#0d1526]' : 'bg-white';
  const mobileMenuBorder = isDark ? 'border-white/10' : 'border-slate-200';
  const userBarBorder = isDark ? 'border-white/5' : 'border-slate-100';
  const iconBtn = isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100';
  const logoutBtn = isDark ? 'text-slate-400 hover:text-rose-400 hover:bg-rose-400/10' : 'text-slate-400 hover:text-rose-500 hover:bg-rose-50';
  const adminBtn = isDark ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-400/10' : 'text-amber-500 hover:text-amber-600 hover:bg-amber-50';
  const superAdminBtn = isDark ? 'text-violet-400 hover:text-violet-300 hover:bg-violet-400/10' : 'text-violet-500 hover:text-violet-600 hover:bg-violet-50';
  const pushBtn = isDark ? 'text-sky-400 hover:text-sky-300 hover:bg-sky-400/10' : 'text-sky-500 hover:text-sky-600 hover:bg-sky-50';
  const loginBtn = isDark ? 'bg-sky-500/20 hover:bg-sky-500/30 text-sky-400' : 'bg-sky-50 hover:bg-sky-100 text-sky-600';
  const contentText = isDark ? 'text-slate-200' : 'text-slate-800';
  const siteTagBg = isDark ? 'bg-sky-500/15 text-sky-400 hover:bg-sky-500/30' : 'bg-sky-50 text-sky-600 hover:bg-sky-100';
  const activeBadge = isDark ? 'bg-rose-500/25 text-rose-300' : 'bg-rose-50 text-rose-500';

  return (
    <div className={`min-h-dvh ${bg} flex flex-col`}>
      {/* SVG 그라데이션 정의 (메뉴 아이콘용) */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="stl-dashboard" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#0891b2" />
          </linearGradient>
          <linearGradient id="stl-ai" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
          {Object.values(SECTION_GRADIENT).map(({ id, colors }) => (
            <linearGradient key={id} id={id} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors[0]} />
              <stop offset="100%" stopColor={colors[1]} />
            </linearGradient>
          ))}
        </defs>
      </svg>
      <header className={`sticky top-0 z-40 ${headerBg} backdrop-blur-xl border-b ${borderColor}`}>
        {/* Row 1: 로고 · 검색 · 유저 정보 */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="h-[52px] flex items-center gap-5">
            {/* 로고: 유통사 로고 있으면 이미지, 없으면 텍스트 이름 */}
            <Link to="/dashboard" className="shrink-0 flex items-center gap-2">
              {distributorLogo
                ? <img src={distributorLogo} alt={distributorName} className="h-7 w-auto" />
                : <span className={`text-base font-bold ${textPrimary}`}>{distributorName || 'p!nk'}</span>
              }
            </Link>

            {/* 검색바 */}
            <div className="flex-1 relative lg:max-w-sm">
              <Search size={15} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${textMuted} pointer-events-none`} />
              <input
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                placeholder="앨범, 아티스트 검색"
                className={`w-full pl-9 pr-8 py-[7px] ${inputBg} border rounded-full text-[13px] focus:outline-none focus:border-sky-500/50 transition-all`}
              />
              {searchValue && (
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearSearch}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${textMuted} ${isDark ? 'hover:text-white' : 'hover:text-slate-700'}`}>
                  <X size={14} />
                </button>
              )}
              <SearchDropdown />
            </div>

            {/* 우측: 유저 정보 */}
            <div className="hidden lg:flex items-center gap-2 ml-auto shrink-0">
              {authLoading ? (
                <div className={`flex items-center gap-2 ${textSecondary} text-sm`}>
                  <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-slate-500' : 'bg-slate-300'} animate-pulse`} />
                </div>
              ) : me ? (
                <div className="flex items-center gap-2">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType="client"
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className={`text-sm font-medium ${textPrimary} max-w-[100px] truncate`}>{me.name}</span>
                  <button
                    title="메시지"
                    className={`relative p-1.5 rounded-md ${iconBtn} transition-colors`}
                    onClick={() => openInbox?.()}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-sky-500' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {me.adminYn === 'Y' && isPink && (
                    <>
                      <button
                        title="푸시 알림 발송"
                        className={`p-1.5 rounded-md ${pushBtn} transition-colors`}
                        onClick={() => navigate('/admin/push-notifications')}>
                        <Bell size={15} />
                      </button>
                      <button
                        title="관리자"
                        className={`p-1.5 rounded-md ${adminBtn} transition-colors`}
                        onClick={() => navigate(`${PUBLIC_BASE}/admin/members`)}>
                        <Shield size={15} />
                      </button>
                    </>
                  )}
                  {me?.superAdminYn === 'Y' && isPink && (
                    <button
                      title="슈퍼관리자"
                      className={`p-1.5 rounded-md ${superAdminBtn} transition-colors`}
                      onClick={() => navigate(isSuperAdminMode ? '/dashboard' : '/super/revenue-analysis')}>
                      <Crown size={15} />
                    </button>
                  )}
                  <button
                    title="프로필 수정"
                    className={`p-1.5 rounded-md ${iconBtn} transition-colors`}
                    onClick={() => navigate('/editProfile')}>
                    <UserCog size={15} />
                  </button>
                  <button
                    title="로그아웃"
                    className={`p-1.5 rounded-md ${logoutBtn} transition-colors`}
                    onClick={logout}>
                    <LogOut size={15} />
                  </button>
                </div>
              ) : (
                <button
                  className={`flex items-center gap-2 px-3 py-1.5 ${loginBtn} rounded-lg transition-colors text-sm font-medium`}
                  onClick={() => navigate('/login')}>
                  <LogIn size={14} />
                  로그인
                </button>
              )}
            </div>

            {/* 모바일 햄버거 */}
            <button
              className={`lg:hidden ml-auto p-2 ${textSecondary} ${isDark ? 'hover:text-white' : 'hover:text-slate-700'} shrink-0`}
              onClick={() => setMobileOpen((v) => !v)}>
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* 행 구분선 */}
        <div className={`border-b ${isDark ? 'border-white/[0.05]' : 'border-slate-100'}`} />

        {/* Row 2: 데스크탑 네비게이션 (정산 메뉴 + 유틸 링크) */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="hidden lg:flex items-center -ml-3 -mb-px nav-anim">
            {isSuperAdminMode ? (
              /* 슈퍼관리자 전용 네비 */
              <>
                <Link
                  to="/super/revenue-analysis"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/super/revenue-analysis' ? navLinkActive : navLinkInactive
                  }`}>
                  <PiggyBank size={14} className="text-emerald-400" />
                  수익분석
                </Link>
                <Link
                  to="/super/carryover"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/super/carryover' ? navLinkActive : navLinkInactive
                  }`}>
                  <ArrowRightLeft size={14} className="text-amber-400" />
                  이월액 현황
                </Link>
                <Link
                  to="/super/tax-invoice"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/super/tax-invoice' ? navLinkActive : navLinkInactive
                  }`}>
                  <FileText size={14} className="text-sky-400" />
                  세금계산서
                </Link>
                {/* 현금영수증 — 팝빌 후불 비용 이슈로 비활성화 (2026-04-07) */}
                <Link
                  to="/super/distributors"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/super/distributors' ? navLinkActive : navLinkInactive
                  }`}>
                  <Building2 size={14} className="text-slate-400" />
                  유통사 관리
                </Link>
                <Link
                  to="/super/promotion-manage"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/super/promotion-manage' ? navLinkActive : navLinkInactive
                  }`}>
                  <Star size={14} className="text-rose-400" />
                  프로모션 관리
                </Link>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${siteTagBg} transition-colors`}>
                    <ChevronRight size={12} className="rotate-180" />
                    일반 정산
                  </button>
                  <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${isDark ? 'bg-violet-500/25 text-violet-300' : 'bg-violet-50 text-violet-500'}`}>
                    <Crown size={11} />
                    슈퍼관리자
                  </span>
                </div>
              </>
            ) : (
              /* 일반 네비 */
              <>
                {/* 대시보드 링크 */}
                <Link
                  to="/dashboard"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/dashboard' ? navLinkActive : navLinkInactive
                  }`}>
                  <LayoutDashboard size={14} style={{ stroke: 'url(#stl-dashboard)' }} />
                  대시보드
                </Link>

                {/* AI 정산분석 */}
                <Link
                  to="/catalog-health"
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                    pathname === '/catalog-health' ? navLinkActive : navLinkInactive
                  }`}>
                  <BrainCircuit size={14} style={{ stroke: 'url(#stl-ai)' }} />
                  AI 분석
                  <span className={`px-1 py-0.5 rounded text-[9px] font-bold leading-none ${isDark ? 'bg-rose-500/20 text-rose-400' : 'bg-rose-50 text-rose-500'}`}>N</span>
                </Link>

                {/* 동적 메뉴 섹션 (방송심의, 커뮤니티 제외) — 직접 링크 */}
                {menuItems.filter((s) => s.name !== '방송심의' && s.name !== '커뮤니티').map((section) => {
                  const SectionIcon = SECTION_ICON_MAP[section.name] || ChevronRight;
                  const gradient = SECTION_GRADIENT[section.name];
                  const active = isSectionActive(section);
                  const firstItem = filterSectionItems(section.items)[0];

                  return (
                    <React.Fragment key={section.name}>
                      <Link
                        to={firstItem?.url || '#'}
                        className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                          active ? navLinkActive : navLinkInactive
                        }`}>
                        <SectionIcon size={14} style={gradient ? { stroke: `url(#${gradient.id})` } : undefined} />
                        {section.name}
                      </Link>
                      {/* 정산리포트 뒤에 홍보리포트 삽입 (관리자) */}
                      {section.name === '정산리포트' && me?.adminYn === 'Y' && isPink && (
                        <Link
                          to="/super/promotion-report"
                          className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                            pathname === '/super/promotion-report' ? navLinkActive : navLinkInactive
                          }`}>
                          <Megaphone size={14} className="text-orange-500" />
                          홍보리포트
                        </Link>
                      )}
                      {section.name === '엑셀업로드' && me?.adminYn === 'Y' && isPink && (
                        <Link
                          to="/notice-manage"
                          className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                            pathname === '/notice-manage' ? navLinkActive : navLinkInactive
                          }`}>
                          <Bell size={14} className="text-rose-400" />
                          공지
                        </Link>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* 홍보리포트 (일반 권리사) */}
                {me?.adminYn !== 'Y' && hasPromotionReport && (
                  <Link
                    to="/promotion-report"
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                      pathname === '/promotion-report' ? navLinkActive : navLinkInactive
                    }`}>
                    <Megaphone size={14} className="text-orange-500" />
                    홍보리포트
                  </Link>
                )}

                {/* 커뮤니티 직접 링크 */}
                {menuItems.find((s) => s.name === '커뮤니티') && (() => {
                  const gradient = SECTION_GRADIENT['커뮤니티'];
                  const communitySection = menuItems.find((s) => s.name === '커뮤니티');
                  const active = isSectionActive(communitySection);
                  return (
                    <Link
                      to="/Notice/List"
                      className={`flex items-center gap-1.5 px-3 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                        active ? navLinkActive : navLinkInactive
                      }`}>
                      <MessageSquare size={14} style={gradient ? { stroke: `url(#${gradient.id})` } : undefined} />
                      커뮤니티
                    </Link>
                  );
                })()}

                {/* 우측: 방송심의(핑크 전용) + 사이트 전환 버튼(핑크 전용) */}
                <div className="flex items-center gap-2 ml-auto">
                  {/* 방송심의 직접 링크 — 핑크 전용 */}
                  {isPink && (() => {
                    const reviewSection = menuItems.find((s) => s.name === '방송심의');
                    if (!reviewSection) return null;
                    const reviewGradient = SECTION_GRADIENT['방송심의'];
                    const reviewActive = isSectionActive(reviewSection);
                    const firstItem = filterSectionItems(reviewSection.items)[0];
                    return (
                      <Link
                        to={firstItem?.url || '#'}
                        className={`flex items-center gap-1.5 px-2 py-2.5 text-[15px] font-semibold transition-colors border-b-2 ${
                          reviewActive ? navLinkActive : navLinkInactive
                        }`}>
                        <Radio size={14} style={reviewGradient ? { stroke: `url(#${reviewGradient.id})` } : undefined} />
                        방송심의
                      </Link>
                    );
                  })()}
                  {isPink && (
                    <>
                      <div className={`w-px h-4 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
                      <Link
                        to={PUBLIC_BASE || '/'}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${siteTagBg} transition-colors`}>
                        홈페이지
                      </Link>
                      <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${activeBadge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-500'} animate-pulse`} />
                        정산시스템
                      </span>
                    </>
                  )}
                </div>
              </>
            )}
          </nav>
        </div>

        {/* 하단 구분선 */}
        <div className={`border-b ${isDark ? 'border-white/[0.06]' : 'border-slate-100'}`} />

        {/* 모바일 네비게이션 (가로 스크롤) */}
        <div className={`lg:hidden overflow-x-auto overflow-y-hidden scrollbar-hide border-b ${isDark ? 'border-white/[0.06]' : 'border-slate-100'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <nav className="flex items-center -mb-px">
              {isSuperAdminMode ? (
                /* 슈퍼관리자 전용 모바일 네비 */
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className={`flex items-center gap-1 px-3 py-2 text-[13px] font-semibold whitespace-nowrap transition-colors border-b-2 border-transparent ${isDark ? 'text-violet-400' : 'text-violet-500'}`}>
                    <ChevronRight size={12} className="rotate-180" />
                    일반정산
                  </button>
                  <Link
                    to="/super/revenue-analysis"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/super/revenue-analysis' ? navLinkActive : navLinkInactive
                    }`}>
                    <PiggyBank size={14} className="text-emerald-400" />
                    수익분석
                  </Link>
                  <Link
                    to="/super/carryover"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/super/carryover' ? navLinkActive : navLinkInactive
                    }`}>
                    <ArrowRightLeft size={14} className="text-amber-400" />
                    이월액 현황
                  </Link>
                  <Link
                    to="/super/tax-invoice"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/super/tax-invoice' ? navLinkActive : navLinkInactive
                    }`}>
                    <FileText size={14} className="text-sky-400" />
                    세금계산서
                  </Link>
                  <Link
                    to="/super/cash-receipt"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/super/cash-receipt' ? navLinkActive : navLinkInactive
                    }`}>
                    <Receipt size={14} className="text-violet-400" />
                    현금영수증
                  </Link>
                  <Link
                    to="/super/distributors"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/super/distributors' ? navLinkActive : navLinkInactive
                    }`}>
                    <Building2 size={14} className="text-slate-400" />
                    유통사 관리
                  </Link>
                  <Link
                    to="/super/promotion-manage"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/super/promotion-manage' ? navLinkActive : navLinkInactive
                    }`}>
                    <Star size={14} className="text-rose-400" />
                    프로모션 관리
                  </Link>
                </>
              ) : (
                /* 일반 모바일 네비 */
                <>
                  {/* 대시보드 */}
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/dashboard' ? navLinkActive : navLinkInactive
                    }`}>
                    <LayoutDashboard size={14} style={{ stroke: 'url(#stl-dashboard)' }} />
                    대시보드
                  </Link>

                  {/* AI 정산분석 */}
                  <Link
                    to="/catalog-health"
                    className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                      pathname === '/catalog-health' ? navLinkActive : navLinkInactive
                    }`}>
                    <BrainCircuit size={14} style={{ stroke: 'url(#stl-ai)' }} />
                    AI 분석
                  </Link>

                  {/* 동적 메뉴 섹션 (커뮤니티, 방송심의 제외) — 직접 링크 */}
                  {menuItems.filter((s) => s.name !== '커뮤니티' && s.name !== '방송심의').map((section) => {
                    const SectionIcon = SECTION_ICON_MAP[section.name] || ChevronRight;
                    const gradient = SECTION_GRADIENT[section.name];
                    const active = isSectionActive(section);
                    const firstItem = filterSectionItems(section.items)[0];

                    return (
                      <React.Fragment key={section.name}>
                        <Link
                          to={firstItem?.url || '#'}
                          className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                            active ? navLinkActive : navLinkInactive
                          }`}>
                          <SectionIcon size={14} style={gradient ? { stroke: `url(#${gradient.id})` } : undefined} />
                          {section.name}
                        </Link>
                        {section.name === '정산리포트' && me?.adminYn === 'Y' && isPink && (
                          <Link
                            to="/super/promotion-report"
                            className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                              pathname === '/super/promotion-report' ? navLinkActive : navLinkInactive
                            }`}>
                            <Megaphone size={14} className="text-orange-500" />
                            홍보리포트
                          </Link>
                        )}
                        {section.name === '엑셀업로드' && me?.adminYn === 'Y' && isPink && (
                          <Link
                            to="/notice-manage"
                            className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                              pathname === '/notice-manage' ? navLinkActive : navLinkInactive
                            }`}>
                            <Bell size={14} className="text-rose-400" />
                            공지
                          </Link>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* 홍보리포트 (일반 권리사) */}
                  {me?.adminYn !== 'Y' && hasPromotionReport && (
                    <Link
                      to="/promotion-report"
                      className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                        pathname === '/promotion-report' ? navLinkActive : navLinkInactive
                      }`}>
                      <Megaphone size={14} className="text-orange-500" />
                      홍보리포트
                    </Link>
                  )}

                  {/* 커뮤니티 직접 링크 */}
                  {menuItems.find((s) => s.name === '커뮤니티') && (() => {
                    const gradient = SECTION_GRADIENT['커뮤니티'];
                    const communitySection = menuItems.find((s) => s.name === '커뮤니티');
                    const active = isSectionActive(communitySection);
                    return (
                      <Link
                        to="/Notice/List"
                        className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                          active ? navLinkActive : navLinkInactive
                        }`}>
                        <MessageSquare size={14} style={gradient ? { stroke: `url(#${gradient.id})` } : undefined} />
                        커뮤니티
                      </Link>
                    );
                  })()}

                  {/* 방송심의 — 핑크 전용 */}
                  {isPink && (() => {
                    const reviewSection = menuItems.find((s) => s.name === '방송심의');
                    if (!reviewSection) return null;
                    const reviewGradient = SECTION_GRADIENT['방송심의'];
                    const reviewActive = isSectionActive(reviewSection);
                    const firstItem = filterSectionItems(reviewSection.items)[0];
                    return (
                      <Link
                        key="방송심의"
                        to={firstItem?.url || '#'}
                        className={`flex items-center gap-1.5 px-3 py-2 text-[15px] font-semibold whitespace-nowrap transition-colors border-b-2 ${
                          reviewActive ? navLinkActive : navLinkInactive
                        }`}>
                        <Radio size={14} style={reviewGradient ? { stroke: `url(#${reviewGradient.id})` } : undefined} />
                        방송심의
                      </Link>
                    );
                  })()}

                </>
              )}
            </nav>
          </div>
        </div>

        {/* 모바일 햄버거 드롭다운 (유저정보, 사이트전환만) */}
        {mobileOpen && (
          <nav className={`lg:hidden border-t ${mobileMenuBorder} px-4 py-3 flex flex-col gap-1 ${mobileMenuBg} max-h-[calc(100dvh-52px)] overflow-y-auto`}>
            {/* 모바일 유저 영역 */}
            {me ? (
              <div className={`flex items-center justify-between px-3 py-3 mb-2 border-b ${userBarBorder}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <UserAvatar
                    name={me.name}
                    profileImage={me.profileImage}
                    userType="client"
                    adminYn={me.adminYn}
                    size={28}
                  />
                  <span className={`text-sm font-medium ${textPrimary} truncate`}>{me.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  <button
                    className={`relative p-1.5 rounded-md ${iconBtn} transition-colors`}
                    onClick={() => { openInbox?.(); setMobileOpen(false); }}>
                    <Mail size={15} className={unreadCount > 0 ? 'text-sky-500' : ''} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {me.adminYn === 'Y' && isPink && (
                    <>
                      <button
                        className={`p-1.5 rounded-md ${pushBtn} transition-colors`}
                        onClick={() => { navigate('/admin/push-notifications'); setMobileOpen(false); }}>
                        <Bell size={15} />
                      </button>
                      <button
                        className={`p-1.5 rounded-md ${adminBtn} transition-colors`}
                        onClick={() => { navigate(`${PUBLIC_BASE}/admin/members`); setMobileOpen(false); }}>
                        <Shield size={15} />
                      </button>
                    </>
                  )}
                  {me?.superAdminYn === 'Y' && isPink && (
                    <button
                      title="슈퍼관리자"
                      className={`p-1.5 rounded-md ${superAdminBtn} transition-colors`}
                      onClick={() => { navigate(isSuperAdminMode ? '/dashboard' : '/super/revenue-analysis'); setMobileOpen(false); }}>
                      <Crown size={15} />
                    </button>
                  )}
                  <button
                    className={`p-1.5 rounded-md ${iconBtn} transition-colors`}
                    onClick={() => { navigate('/editProfile'); setMobileOpen(false); }}>
                    <UserCog size={15} />
                  </button>
                  <button
                    className={`p-1.5 rounded-md ${logoutBtn} transition-colors`}
                    onClick={() => { logout(); setMobileOpen(false); }}>
                    <LogOut size={15} />
                  </button>
                </div>
              </div>
            ) : (
              <div className={`px-3 py-3 mb-2 border-b ${userBarBorder}`}>
                <button
                  className={`flex items-center gap-2 px-3 py-1.5 ${loginBtn} rounded-lg transition-colors text-sm font-medium`}
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}>
                  <LogIn size={14} />
                  로그인
                </button>
              </div>
            )}

            {/* 모바일 사이트 전환 버튼 — 핑크 전용 */}
            {isPink && (
              <div className={`flex items-center gap-2 px-3 pt-3 mt-2 border-t ${userBarBorder}`}>
                <Link
                  to={PUBLIC_BASE || '/'}
                  onClick={() => setMobileOpen(false)}
                  className={`flex-1 text-center px-3 py-2 rounded-lg text-xs font-semibold ${siteTagBg} transition-colors`}>
                  홈페이지
                </Link>
                <span className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${activeBadge}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-rose-400' : 'bg-rose-500'} animate-pulse`} />
                  정산시스템
                </span>
              </div>
            )}
          </nav>
        )}
      </header>

      {/* 햄버거 백드롭 */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* 콘텐츠 영역 */}
      <main className={`flex-1 ${contentText}`} {...(!isDark && { 'data-theme': 'light' })}>
        <div key={pathname} className="page-enter max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8">
          <Outlet />
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default SettlementLayout;
